/*
 * product-form.js — Aether
 * Standalone ES module (no Liquid). Registers three custom elements:
 *   <variant-selects>  — variant option UI → updates URL, price, availability, buy button
 *   <quantity-input>   — accessible +/- quantity stepper
 *   <product-form>     — AJAX add-to-cart with progressive-enhancement fallback
 *
 * Dependency-free. Uses the Section Rendering API for price/availability HTML swap
 * with a local product-variants JSON fallback. Dispatches `cart:refresh` on document.
 */

const guard = (tag, cls) => {
  if (!customElements.get(tag)) customElements.define(tag, cls);
};

/* -------------------------------------------------------------------------- */
/* quantity-input                                                             */
/* -------------------------------------------------------------------------- */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;
    this.changeEvent = new Event('change', { bubbles: true });

    this.addEventListener('click', (event) => {
      const btn = event.target.closest('button[data-quantity-step]');
      if (!btn) return;
      event.preventDefault();
      this.step(parseInt(btn.dataset.quantityStep, 10));
    });

    this.input.addEventListener('change', () => this.validate());
  }

  step(delta) {
    const current = parseInt(this.input.value, 10) || 0;
    const min = parseInt(this.input.min, 10) || 1;
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    let next = current + delta;
    if (next < min) next = min;
    if (next > max) next = max;
    if (next !== current) {
      this.input.value = next;
      this.input.dispatchEvent(this.changeEvent);
    }
  }

  validate() {
    const min = parseInt(this.input.min, 10) || 1;
    const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
    let value = parseInt(this.input.value, 10);
    if (isNaN(value) || value < min) value = min;
    if (value > max) value = max;
    this.input.value = value;
  }
}
guard('quantity-input', QuantityInput);

/* -------------------------------------------------------------------------- */
/* variant-selects                                                            */
/* -------------------------------------------------------------------------- */
class VariantSelects extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.url;
    this.productHandle = this.dataset.handle;
    this.variants = this.getVariantData();

    this.addEventListener('change', this.onVariantChange.bind(this));
    // Establish initial state from the pre-selected inputs (no fetch on load).
    this.updateSelectedVariant();
  }

  getVariantData() {
    const script = this.querySelector('script[type="application/json"][data-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return [];
    }
  }

  /* Read every option group (fieldset) in index order; handles select OR radios. */
  get selectedOptions() {
    const values = [];
    this.querySelectorAll('[data-option-index]').forEach((group) => {
      const index = parseInt(group.dataset.optionIndex, 10);
      let value = null;
      const select = group.querySelector('select');
      if (select) {
        value = select.value;
      } else {
        const checked = group.querySelector('input[type="radio"]:checked');
        if (checked) value = checked.value;
      }
      values[index] = value;
    });
    return values;
  }

  updateSelectedVariant() {
    const selected = this.selectedOptions;
    this.currentVariant =
      this.variants.find((variant) =>
        variant.options.every((option, i) => option === selected[i])
      ) || null;
    return this.currentVariant;
  }

  onVariantChange() {
    this.updateSelectedVariant();
    this.updatePillLabels();

    if (!this.currentVariant) {
      this.setUnavailable();
      return;
    }

    this.updateURL();
    this.updateVariantIdInput();
    this.renderSectionUpdate();
  }

  /* Reflect the checked pill/radio label state for styling + a11y. */
  updatePillLabels() {
    this.querySelectorAll('.variant-option__input').forEach((input) => {
      const label = input.closest('label');
      if (label) label.setAttribute('aria-checked', input.checked ? 'true' : 'false');
    });
  }

  updateURL() {
    if (!this.currentVariant || !this.productUrl) return;
    const url = new URL(this.productUrl, window.location.origin);
    url.searchParams.set('variant', this.currentVariant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateVariantIdInput() {
    const input = document.querySelector(`#product-form-${this.sectionId} [name="id"]`);
    if (input) {
      input.value = this.currentVariant.id;
      // Re-enable the id field (it ships disabled when the initial variant is sold out).
      if (this.currentVariant.available) input.removeAttribute('disabled');
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  setUnavailable() {
    const button = this.getBuyButton();
    if (button) {
      button.setAttribute('disabled', 'disabled');
      const text = button.querySelector('[data-button-text]') || button;
      text.textContent = button.dataset.unavailableText || 'Unavailable';
    }
  }

  getBuyButton() {
    return document.querySelector(`#product-form-${this.sectionId} [data-add-to-cart]`);
  }

  /* Prefer the Section Rendering API; if it fails, fall back to local JSON. */
  async renderSectionUpdate() {
    if (!this.sectionId || !this.productHandle) {
      this.updateFromLocalData();
      return;
    }
    try {
      const url = `${this.productUrl}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('bad response');
      const text = await res.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.swap(html, '[data-price-wrapper]');
      this.swap(html, `#buy-buttons-${this.sectionId}`);
      this.swap(html, `#sku-${this.sectionId}`);
    } catch (e) {
      this.updateFromLocalData();
    }
  }

  swap(sourceDoc, selector) {
    const incoming = sourceDoc.querySelector(selector);
    const current = document.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  }

  /* No-network fallback: toggle price + button from embedded JSON. */
  updateFromLocalData() {
    const v = this.currentVariant;
    const wrapper = document.querySelector('[data-price-wrapper]');
    if (wrapper && v.price_formatted) {
      const sale = wrapper.querySelector('[data-price-sale]');
      const regular = wrapper.querySelector('[data-price-regular]');
      const compare = wrapper.querySelector('[data-price-compare]');
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      if (regular) {
        regular.textContent = v.price_formatted;
        regular.classList.toggle('visually-hidden', onSale);
      }
      if (sale) {
        sale.textContent = v.price_formatted;
        sale.classList.toggle('visually-hidden', !onSale);
      }
      if (compare) {
        compare.textContent = v.compare_at_price_formatted || '';
        compare.classList.toggle('visually-hidden', !onSale);
      }
    }

    const button = this.getBuyButton();
    if (button) {
      const text = button.querySelector('[data-button-text]') || button;
      if (v.available) {
        button.removeAttribute('disabled');
        text.textContent = button.dataset.addText || 'Add to cart';
      } else {
        button.setAttribute('disabled', 'disabled');
        text.textContent = button.dataset.soldOutText || 'Sold out';
      }
    }
  }
}
guard('variant-selects', VariantSelects);

/* -------------------------------------------------------------------------- */
/* product-form                                                               */
/* -------------------------------------------------------------------------- */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.querySelector('[data-add-to-cart]');
    this.errorTarget = this.querySelector('[data-cart-error]');
    this.statusTarget = this.querySelector('[data-cart-status]');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    // Progressive enhancement: if fetch is unavailable, let the native POST run.
    if (typeof fetch !== 'function') return;
    event.preventDefault();

    if (this.submitButton?.getAttribute('disabled')) return;
    this.setLoading(true);
    this.clearError();

    const formData = new FormData(this.form);
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.showError(data.description || data.message || 'Could not add to cart.');
        return;
      }

      this.showStatus(this.dataset.addedText || 'Added to cart');
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { source: 'product-form', product: data, sectionId: this.dataset.section },
        })
      );
    } catch (e) {
      this.showError('Could not add to cart. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(state) {
    if (!this.submitButton) return;
    this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
    this.submitButton.classList.toggle('is-loading', state);
    // Do not disable permanently — only during the request.
    if (state) this.submitButton.setAttribute('disabled', 'disabled');
    else this.submitButton.removeAttribute('disabled');
  }

  clearError() {
    if (this.errorTarget) {
      this.errorTarget.hidden = true;
      this.errorTarget.textContent = '';
    }
  }

  showError(message) {
    if (this.errorTarget) {
      this.errorTarget.textContent = message;
      this.errorTarget.hidden = false;
    }
    if (this.statusTarget) this.statusTarget.textContent = '';
  }

  showStatus(message) {
    if (this.statusTarget) this.statusTarget.textContent = message;
  }
}
guard('product-form', ProductForm);
